import { useContext } from 'react';
import { QueryFunctionContext, useQuery, UseQueryOptions } from 'react-query';
import { assignment_api } from '@axo/shared/data-access/api';
import { assignment } from '@axo/shared/data-access/types';
import { DataAccessContext, useAPI } from '@axo/shared/data-access/provider';
import {
  assignmentKeys,
  parseAssignmentKey,
} from '../../cache-keys/assignmentKeys';
import { queryFunction } from '../../query-function';

export function useActiveAssignments(marketCountry: string) {
  return useActiveAssignmentsQuery<assignment.FindAssignmentsResult>(
    marketCountry
  );
}

function activeAssignmentsByCountryPaarms({
  queryKey,
}: QueryFunctionContext<ReturnType<(typeof assignmentKeys)['active']>>) {
  const { marketCountry } = parseAssignmentKey(queryKey);
  return {
    Country: marketCountry,
    Processed: false,
    Assigned: true,
  };
}

function useActiveAssignmentsQuery<T>(
  marketCountry: string,
  options?: Omit<
    UseQueryOptions<
      assignment.FindAssignmentsResult,
      unknown,
      T,
      ReturnType<(typeof assignmentKeys)['active']>
    >,
    'queryKey' | 'queryFn' | 'enabled'
  >
) {
  const { state } = useContext(DataAccessContext);
  const api = useAPI();
  const cacheKey = assignmentKeys.active({
    marketCountry,
  });
  return useQuery(
    cacheKey,
    queryFunction(assignment_api.find(api), activeAssignmentsByCountryPaarms),
    {
      enabled: state.user.token.length > 0,
      ...options,
    }
  );
}

export interface AssignmentCount {
  User: string;
  NumAssignments: number;
}

export interface CountAssignmentsResult {
  Items: AssignmentCount[];
}

type AssignmentWithUser = assignment.Assignment & {
  User: string;
};

export function useCountAssignments(marketCountry: string) {
  return useActiveAssignmentsQuery(marketCountry, {
    select: (data) => {
      const usersWithCounts = data.Items.filter(
        (x): x is AssignmentWithUser => x.User !== null
      ).reduce(
        (acc, x) => ({
          ...acc,
          [x.User]: (acc[x.User] || 0) + 1,
        }),
        {} as { [user: string]: number }
      );
      const Items: AssignmentCount[] = Object.entries(usersWithCounts).map(
        ([user, num]) => ({
          User: user,
          NumAssignments: num,
        })
      );
      const response: CountAssignmentsResult = { Items };
      return response;
    },
  });
}
